'use client';

import { ReactNode } from 'react';
import Markdown from 'react-markdown';

import {
    Align,
    BoxContent,
    Countdown,
    Divider,
    FlexBox,
    Image,
    Justify,
    Price,
    PriceLevel,
    Spacer,
    SpacerSize,
    Sticker,
    Text,
    TextLevels,
    TypographyColor,
    VariantState,
    View,
} from '@bytel/trilogy-react';

import { ConfigType } from '@app-types/api';
import { FaiProduct } from '@app-types/fai';
import { PlanType } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';

import { calculFinalPrice } from '@helpers/utils';

import { useHome } from '@landing/hooks';

type BannerPriceProps = {
    icon: ReactNode;
    baseProduct?: PlanType | FaiProduct;
    isFai?: boolean;
    isLoading?: boolean;
    config?: ConfigType;
};

export function BAndyouPrice({
    icon,
    baseProduct,
    isFai = false,
    isLoading = false,
    config,
}: Readonly<BannerPriceProps>) {
    const { selectedNetwork, selectedFaiProduct } = useHome();
    const newPrice = calculFinalPrice(selectedNetwork, baseProduct, selectedFaiProduct);
    const animco = baseProduct?.animco;
    const timerStartDate = animco?.startDateTimer ? new Date(animco.startDateTimer) : null;
    const timerDeadline = animco?.endDateTimer ? new Date(animco.endDateTimer) : null;
    const now = new Date();

    const shouldShowTimer = timerDeadline && now < timerDeadline && (!timerStartDate || now >= timerStartDate);
    const shouldShowSticker = !!animco?.sticker;
    const shouldShowPromo = !!animco?.promoOffer;
    const shouldShowDivider = !!animco?.divider;
    const backgroundImage = isFai ? config?.fibre_image?.public_url : config?.forfait_mobile_image?.public_url;
    const specialImage = isFai ? undefined : (baseProduct as PlanType)?.bundle_image;

    return (
        <BoxContent backgroundSrc={backgroundImage} backgroundColor='MAIN'>
            <View className={isLoading ? 'is-loading' : ''}>
                {shouldShowTimer ? (
                    <>
                        <View flexable justify={Justify.SPACE_BETWEEN}>
                            <Countdown small className='has-background-white' deadline={timerDeadline} />
                            <Spacer size={SpacerSize.FIVE} />
                            {icon && (
                                <FlexBox
                                    justify={Justify.CENTER}
                                    align={Align.CENTER}
                                    // @ts-expect-error - work with "other" props
                                    style={{ height: '24px' }}
                                    className='is-marginless has-text-white'
                                >
                                    {icon}
                                </FlexBox>
                            )}
                        </View>
                        <Price inverted amount={newPrice.now} period='mois' level={PriceLevel.THREE} />
                    </>
                ) : (
                    <View flexable justify={Justify.SPACE_BETWEEN}>
                        <Price inverted amount={newPrice.now} period='mois' level={PriceLevel.THREE} />

                        {icon ? (
                            <FlexBox
                                justify={Justify.CENTER}
                                align={Align.CENTER}
                                // @ts-expect-error - work with "other" props
                                style={{ height: '24px' }}
                                className='is-marginless has-text-white'
                            >
                                {icon}
                            </FlexBox>
                        ) : (
                            <View />
                        )}
                    </View>
                )}

                {newPrice?.duration ? (
                    <Text inverted level={TextLevels.ONE} marginless>
                        Pendant {newPrice.duration} mois puis <InlinePrice amount={newPrice?.forever} period='mois' />
                    </Text>
                ) : null}
                <FlexBox align={Align.CENTER} justify={Justify.SPACE_BETWEEN} className='is-fullwidth'>
                    <Text inverted level={TextLevels.ONE} marginless>
                        Sans engagement
                    </Text>
                    {specialImage && (
                        <Image
                            src={specialImage.public_url}
                            alt={specialImage.title ?? ''}
                            className='plan-header-special-image'
                        />
                    )}
                </FlexBox>

                {shouldShowDivider && <Divider inverted />}

                {shouldShowSticker && <Sticker variant={VariantState.ACCENT} label={animco.sticker ?? ''} small />}
                {shouldShowPromo && (
                    <Text level={TextLevels.TWO} typo={TypographyColor.TEXT_WHITE}>
                        <Markdown components={{ p: 'span' }}>{animco.promoOffer}</Markdown>
                    </Text>
                )}
            </View>
        </BoxContent>
    );
}
